// Toasts and dialogs driven by the game's prompts, plus the bottom sheet.
import { createPortal } from "react-dom";
import { useEffect, type ReactNode } from "react";
import { usePrompts } from "@/state/prompts";
import { Button, Card, cx } from "./primitives";

export const Toaster = () => {
  const toasts = usePrompts((s) => s.toasts);
  if (!toasts.length) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={cx("toast", t.kind !== "info" && t.kind)}>{t.text}</div>
      ))}
    </div>
  );
};

export const DialogHost = () => {
  const dialog = usePrompts((s) => s.dialog);
  const close = usePrompts((s) => s.dialogClose);
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, close]);
  if (!dialog) return null;
  return (
    <div className="scrim center" onClick={close} role="presentation">
      <Card className="dialog" role="alertdialog" aria-labelledby="dlg-t" aria-describedby="dlg-d" onClick={(e) => e.stopPropagation()}>
        <h3 id="dlg-t">{dialog.title}</h3>
        <p id="dlg-d">{dialog.text}</p>
        <div className="acts">
          <Button kind="steel" size="md" onClick={close} autoFocus>OK</Button>
        </div>
      </Card>
    </div>
  );
};

export const Sheet = ({ open, onClose, title, children, label, from = "bottom", anchor }: { open: boolean; onClose: () => void; title?: ReactNode; label: string; children: ReactNode; /** "top" drops it down from just below the world tab; "header" from just below the whole header (map bookmarks) */ from?: "bottom" | "top" | "header";
  /** a drop-down that hangs straight off this element (CSS selector), covering what is below it — the world header's
   *  city switcher and announcements hang off the menu bar, the world switcher off the World tab (Ben, Oct 10 2026).
   *  When the element isn't on the screen (the Portal's header) the `from` rule applies. */
  anchor?: string }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  // a drop-down (world / city switchers) hangs just below the world tab ("All Worlds" / "World 2") at the top of the
  // header, rather than from the very top of the screen; the tab itself stays clear of the dimming
  // (below the red gem under the tab, so the tab and its gem both stay clear)
  // (the slim header of a phone on its side has no gem: the tab itself, then)
  const rectOf = (sel: string) => {
    const r = document.querySelector(sel)?.getBoundingClientRect();
    return r && r.height > 0 ? r : undefined;
  };
  // the world header (strip + menu bar): everything drops from under the whole bar; Portal's header: from the gem / tab
  const anchored = from !== "bottom" && anchor ? rectOf(anchor) : undefined;
  const tab = anchored ?? (from === "bottom" ? undefined : from === "header" || document.querySelector(".hdr.whdr") ? rectOf(".hdr") : rectOf(".hdr .gem") ?? rectOf(".hdr .wtab"));
  const top = tab ? Math.max(0, Math.round(tab.bottom + (anchored ? 0 : from === "header" ? 6 : 2))) : 0;
  // rendered into <body>: a sheet opened from inside a card or a stage would otherwise sit in that element's stacking
  // context, under the nav
  return createPortal(
    <div className={from === "bottom" ? "scrim" : cx("scrim top", anchored && "anchored")} onClick={onClose} role="presentation" style={top ? { ["--drop-top" as string]: `${top}px` } : undefined}>
      <Card className="sheet" role="dialog" aria-label={label} onClick={(e) => e.stopPropagation()}>
        {title}
        {children}
      </Card>
    </div>,
    document.body,
  );
};

/** A confirm sheet: what will happen, in words, with one crimson (or gold) action. */
export const Confirm = ({ open, onClose, title, text, action, kind = "crimson", onConfirm, busy }: {
  open: boolean; onClose: () => void; title: ReactNode; text: ReactNode; action: ReactNode; kind?: "crimson" | "gold" | "steel"; onConfirm: () => void; busy?: boolean;
}) => (
  <Sheet open={open} onClose={onClose} label={typeof title === "string" ? title : "Confirm"}>
    <div className="dialog" style={{ padding: 0 }}>
      <h3>{title}</h3>
      <p>{text}</p>
      <div className="acts" style={{ justifyContent: "stretch" }}>
        <Button kind="steel" size="md" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
        <Button kind={kind} size="md" onClick={onConfirm} busy={busy} style={{ flex: 2 }}>{action}</Button>
      </div>
    </div>
  </Sheet>
);
